import { Action, IRowsVerified, Type } from "../actions/FlightPairingStatusV2/FlightPairingStatusV2.Actions";
import { dayKeyRange } from "src/pages/FlightPairingStatusV2/pairingStatusSelectors";
import {
  IEpochRange,
  IFlightPairingStatusMeta,
  IFlightPairingStatusRow,
} from "src/pages/FlightPairingStatusV2/pairingStatusTypes";

export interface IFlightPairingStatusV2State {
  /** Cached rows for the loaded range, by flight id. */
  rows: Record<string, IFlightPairingStatusRow>;
  /** Range the rows were loaded for. */
  range: IEpochRange | null;
  /** Range of the most recent load request (equals `range` once that load has finished). */
  requestedRange: IEpochRange | null;
  isLoading: boolean;
  isRecomputing: boolean;
  loadError: string | null;
  /** When the client last loaded the range. */
  loadedAt: number | null;
  /** Server-side freshness information for the loaded range. */
  meta: IFlightPairingStatusMeta | null;
  /** When the most recent live (websocket) update was applied. */
  lastUpdateAt: number | null;
  selectedFlightId: string | null;
  /** Id of the most recent load request; responses for older requests are ignored. */
  activeRequestId: number;
  /** Number of open Flight Pairing Status windows; the slice resets when the last one closes. */
  openWindows: number;
}

export const initialFlightPairingStatusV2State: IFlightPairingStatusV2State = {
  rows: {},
  range: null,
  requestedRange: null,
  isLoading: false,
  isRecomputing: false,
  loadError: null,
  loadedAt: null,
  meta: null,
  lastUpdateAt: null,
  selectedFlightId: null,
  activeRequestId: 0,
  openWindows: 0,
};

const indexRows = (rows: IFlightPairingStatusRow[]): Record<string, IFlightPairingStatusRow> => {
  const byId: Record<string, IFlightPairingStatusRow> = {};
  for (const row of rows) byId[row.flightId] = row;
  return byId;
};

const sameRange = (a: IEpochRange | null, b: IEpochRange | null): boolean =>
  !!a && !!b && a.start === b.start && a.end === b.end;

/** Epoch ms of a row's evaluation (NaN when it has none). */
const computedAtMs = (row: IFlightPairingStatusRow): number => (row.computedAt ? Date.parse(row.computedAt) : NaN);

/** Newest and oldest evaluation among the rows (null when there is none). */
const evaluationBounds = (
  rows: Record<string, IFlightPairingStatusRow>
): { newest: number | null; oldest: number | null } => {
  let newest = -Infinity;
  let oldest = Infinity;
  for (const row of Object.values(rows)) {
    const ms = computedAtMs(row);
    if (ms > newest) newest = ms;
    if (ms < oldest) oldest = ms;
  }
  return { newest: Number.isFinite(newest) ? newest : null, oldest: Number.isFinite(oldest) ? oldest : null };
};

/**
 * Meta after live rows were upserted or removed. When every row of the range was held (all pages are loaded, so
 * normally it is), the newest and oldest evaluation come from the resulting rows, as the server computes them;
 * otherwise only the newest moves up to the update.
 */
const liveMeta = (
  meta: IFlightPairingStatusMeta,
  heldAll: boolean,
  rows: Record<string, IFlightPairingStatusRow>,
  updatedAt: number
): IFlightPairingStatusMeta => {
  const count = Object.keys(rows).length;
  if (!heldAll) return { ...meta, count, lastComputedAt: Math.max(meta.lastComputedAt ?? 0, updatedAt) };
  const { newest, oldest } = evaluationBounds(rows);
  return { ...meta, count, lastComputedAt: newest, oldestComputedAt: oldest };
};

const holdsAllRows = (state: IFlightPairingStatusV2State): boolean =>
  !!state.meta && Object.keys(state.rows).length === state.meta.count;

/**
 * Meta after a scheduled verification. The server reports the newest / oldest of the per-row evaluations, so the
 * newest moves up when any row lies in the verified days. The oldest is the oldest row when every row of the range
 * is held here (all pages are loaded, so normally they are); otherwise it is only raised, and only when the
 * verified days cover the whole range.
 */
const verifiedMeta = (
  meta: IFlightPairingStatusMeta,
  rows: Record<string, IFlightPairingStatusRow>,
  hasVerifiedRows: boolean,
  { dayKeyStart, dayKeyEnd, verifiedAt, window }: IRowsVerified
): IFlightPairingStatusMeta => {
  const lastComputedAt = hasVerifiedRows ? Math.max(meta.lastComputedAt ?? 0, verifiedAt) : meta.lastComputedAt;
  let oldestComputedAt = meta.oldestComputedAt;
  const all = Object.values(rows);
  if (all.length > 0 && all.length === meta.count) {
    let oldest = Infinity;
    for (const row of all) {
      const ms = computedAtMs(row);
      if (ms < oldest) oldest = ms;
    }
    if (Number.isFinite(oldest)) oldestComputedAt = oldest;
  } else if (Number.isFinite(oldestComputedAt)) {
    const loaded = dayKeyRange(meta.range);
    if (loaded.start >= dayKeyStart && loaded.end <= dayKeyEnd)
      oldestComputedAt = Math.max(oldestComputedAt, verifiedAt);
  }
  // The refresh reports the window it keeps fresh; it moves a day at a time, so follow it.
  const scheduledWindow =
    window && (window.start !== meta.scheduledWindow?.start || window.end !== meta.scheduledWindow?.end)
      ? { start: window.start, end: window.end }
      : meta.scheduledWindow;
  if (
    lastComputedAt === meta.lastComputedAt &&
    oldestComputedAt === meta.oldestComputedAt &&
    scheduledWindow === meta.scheduledWindow
  )
    return meta;
  return { ...meta, lastComputedAt, oldestComputedAt, scheduledWindow };
};

/**
 * A scheduled refresh evaluated every flight filed under the verified UTC days but rewrites only the rows that
 * changed. Mirror the server's GET: a row's evaluation is the later of its own write and its day's last scheduled
 * evaluation (reason "scheduled" when the latter). Returns the same state when nothing changes.
 */
const applyRowsVerified = (state: IFlightPairingStatusV2State, action: IRowsVerified): IFlightPairingStatusV2State => {
  const { dayKeyStart, dayKeyEnd, verifiedAt } = action;
  const verifiedIso = new Date(verifiedAt).toISOString();
  let rows = state.rows;
  let hasVerifiedRows = false;
  for (const [flightId, row] of Object.entries(state.rows)) {
    if (row.dayKey < dayKeyStart || row.dayKey > dayKeyEnd) continue;
    hasVerifiedRows = true;
    if (computedAtMs(row) >= verifiedAt) continue;
    if (rows === state.rows) rows = { ...state.rows };
    rows[flightId] = { ...row, computedAt: verifiedIso, computeReason: "scheduled" };
  }
  const meta = state.meta ? verifiedMeta(state.meta, rows, hasVerifiedRows, action) : state.meta;
  if (rows === state.rows && meta === state.meta) return state;
  return { ...state, rows, meta };
};

export const FlightPairingStatusV2Reducer = (
  state: IFlightPairingStatusV2State = initialFlightPairingStatusV2State,
  action: Action
): IFlightPairingStatusV2State => {
  switch (action.type) {
    case Type.FPS_LOAD_STARTED: {
      // A visible load of a different range clears the old rows so the previous month is not shown (and
      // counted) under the new heading while the new one loads.
      const clearRows = !action.silent && !sameRange(state.range, action.range);
      return {
        ...state,
        rows: clearRows ? {} : state.rows,
        meta: clearRows ? null : state.meta,
        lastUpdateAt: clearRows ? null : state.lastUpdateAt,
        selectedFlightId: clearRows ? null : state.selectedFlightId,
        requestedRange: action.range,
        activeRequestId: action.requestId,
        isLoading: action.silent ? state.isLoading : true,
        loadError: null,
      };
    }
    case Type.FPS_LOAD_SUCCEEDED: {
      if (action.requestId !== state.activeRequestId) return state;
      const rows = indexRows(action.rows);
      return {
        ...state,
        rows,
        range: action.range,
        requestedRange: action.range,
        isLoading: false,
        loadError: null,
        loadedAt: action.loadedAt,
        meta: action.meta ?? state.meta,
        selectedFlightId: state.selectedFlightId && rows[state.selectedFlightId] ? state.selectedFlightId : null,
      };
    }
    case Type.FPS_LOAD_FAILED:
      if (action.requestId !== state.activeRequestId) return state;
      return { ...state, isLoading: false, loadError: action.error };
    case Type.FPS_RECOMPUTE_STARTED:
      return { ...state, isRecomputing: true };
    case Type.FPS_RECOMPUTE_FINISHED:
      return { ...state, isRecomputing: false };
    case Type.FPS_ROWS_UPSERTED: {
      if (action.rows.length === 0) return state;
      const rows = { ...state.rows, ...indexRows(action.rows) };
      return {
        ...state,
        rows,
        lastUpdateAt: action.updatedAt,
        meta: state.meta ? liveMeta(state.meta, holdsAllRows(state), rows, action.updatedAt) : state.meta,
      };
    }
    case Type.FPS_ROWS_REMOVED: {
      const rows = { ...state.rows };
      let removed = false;
      for (const id of action.flightIds) {
        if (rows[id]) {
          delete rows[id];
          removed = true;
        }
      }
      if (!removed) return state;
      return {
        ...state,
        rows,
        lastUpdateAt: action.updatedAt,
        meta: state.meta
          ? holdsAllRows(state)
            ? liveMeta(state.meta, true, rows, action.updatedAt)
            : { ...state.meta, count: Object.keys(rows).length }
          : state.meta,
        selectedFlightId:
          state.selectedFlightId && action.flightIds.includes(state.selectedFlightId) ? null : state.selectedFlightId,
      };
    }
    case Type.FPS_ROWS_VERIFIED:
      // Not a data change: lastUpdateAt is left alone.
      return applyRowsVerified(state, action);
    case Type.FPS_FLIGHT_SELECTED:
      return { ...state, selectedFlightId: action.flightId };
    case Type.FPS_WINDOW_OPENED:
      return { ...state, openWindows: state.openWindows + 1 };
    case Type.FPS_WINDOW_CLOSED: {
      const openWindows = Math.max(0, state.openWindows - 1);
      // No window left: drop the data so websocket updates stop reloading a range nobody is looking at.
      if (openWindows === 0)
        return { ...initialFlightPairingStatusV2State, activeRequestId: state.activeRequestId + 1 };
      return { ...state, openWindows };
    }
    default:
      return state;
  }
};
